import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  MARKETPLACES,
  addSetEntries,
  getMarketFreshness,
  getSet,
  getVariants,
  messageFor,
  setEntryLifecycle,
  triggerCollection,
  type NewSetEntry,
  type SetEntry,
} from "./api";
import { badgeVariant, marketplaceLabel, statusTone, variantRowLabel } from "./format";

const EMPTY_FORM = {
  variant_id: "",
  marketplace: "UK",
  competitor_asin: "",
  competitor_brand: "",
  competitor_product: "",
  size_metric: "",
  quantity: "",
  is_self: false,
};

/**
 * Curating which competitor ASINs benchmark each of our variants.
 *
 * NO PERMISSION BRANCH LIVES HERE (I-7). Meteoric passed a `canManage` prop down
 * and hid the form and the row buttons with it. In the portal `ShellPage` wraps
 * every module in the shell-owned `PermissionGate`, which makes edit controls
 * genuinely inert under a `view` decision AND states why in one place. A
 * module-local `decision === "edit"` branch would be a second permission
 * renderer, and two renderers drift.
 */
export function SetTab({ areaName }: { areaName: string }) {
  const client = useQueryClient();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const entries = useQuery({
    queryKey: ["meteoric_competitor_benchmark", "set", includeArchived],
    queryFn: () => getSet(includeArchived),
  });

  const variants = useQuery({
    queryKey: ["meteoric_competitor_benchmark", "variants"],
    queryFn: getVariants,
  });

  const freshness = useQuery({
    queryKey: ["meteoric_competitor_benchmark", "market-freshness"],
    queryFn: getMarketFreshness,
  });

  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ["meteoric_competitor_benchmark"] });
  };

  const fail = (err: unknown) => setError(messageFor(err, areaName));

  const add = useMutation({
    mutationFn: (entry: NewSetEntry) => addSetEntries([entry]),
    onSuccess: () => {
      // The marketplace and variant are KEPT: adding competitors is a batch job,
      // and resetting those two turns "add five ASINs for DE" into five
      // re-selections.
      setForm({ ...EMPTY_FORM, marketplace: form.marketplace, variant_id: form.variant_id });
      setNotice("Competitor added. It is collected on the next daily run, or trigger a collection now.");
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const lifecycle = useMutation({
    mutationFn: ({ entry, action }: { entry: SetEntry; action: "archive" | "restore" }) =>
      setEntryLifecycle(entry.entry_id, action),
    onSuccess: (_data, { action }) => {
      setNotice(
        action === "archive"
          ? "Entry archived. History and every past aggregate it contributed to are kept — nothing is ever deleted."
          : "Entry restored to active collection.",
      );
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const collect = useMutation({
    mutationFn: triggerCollection,
    onSuccess: () => {
      setNotice("Collection triggered. Freshness updates when the run completes, which takes a few minutes.");
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const busy = add.isPending || lifecycle.isPending || collect.isPending;

  const variantLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const v of variants.data ?? []) {
      map.set(v.variant_id, variantRowLabel(v.product, v.variant, v.variant_id));
    }
    return map;
  }, [variants.data]);

  const grouped = useMemo(() => {
    const byMarket = new Map<string, SetEntry[]>();
    for (const entry of entries.data ?? []) {
      const list = byMarket.get(entry.marketplace) ?? [];
      list.push(entry);
      byMarket.set(entry.marketplace, list);
    }
    return [...byMarket.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [entries.data]);

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const entry: NewSetEntry = {
      variant_id: form.variant_id,
      marketplace: form.marketplace,
      competitor_asin: form.competitor_asin.trim().toUpperCase(),
      is_self: form.is_self,
    };
    // Optional fields are OMITTED when blank rather than sent as "". An empty
    // string is a value; it would overwrite a brand the collector later learns.
    if (form.competitor_brand.trim()) entry.competitor_brand = form.competitor_brand.trim();
    if (form.competitor_product.trim()) entry.competitor_product = form.competitor_product.trim();
    if (form.size_metric.trim()) entry.size_metric = form.size_metric.trim();
    if (form.quantity.trim()) entry.quantity = Number(form.quantity);
    add.mutate(entry);
  };

  return (
    <div className="flex flex-col gap-6">
      <p className="max-w-prose text-sm text-muted-foreground">
        Which competitor ASINs benchmark each Ketosource variant, per marketplace. Archiving keeps the
        history; nothing is ever deleted.
      </p>

      {freshness.data && freshness.data.length > 0 && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Collection freshness
            </span>
            {freshness.data.map((m) => (
              <Badge key={m.marketplace} variant={badgeVariant(statusTone(m.freshness_status))}>
                {marketplaceLabel(m.marketplace)} · {m.last_collected_at ?? "never collected"}
              </Badge>
            ))}
            <Button
              className="ml-auto"
              onClick={() => collect.mutate()}
              disabled={busy}
            >
              Collect now
            </Button>
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert>
          <AlertTitle>That did not go through</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {notice && !error && (
        <Alert>
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent>
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <h2 className="text-sm font-semibold text-ks-ink">Add a competitor</h2>
            <div className="flex flex-wrap items-end gap-3">
              <Field label="Our variant" htmlFor="cb-variant">
                <select
                  id="cb-variant"
                  required
                  className="min-h-11 rounded-ks-sm border border-ks-grey-3 bg-ks-surface px-2 text-ks-ink"
                  value={form.variant_id}
                  onChange={(e) => setForm({ ...form, variant_id: e.target.value })}
                >
                  <option value="">Select a variant</option>
                  {(variants.data ?? []).map((v) => (
                    <option key={v.variant_id} value={v.variant_id}>
                      {variantRowLabel(v.product, v.variant, v.variant_id)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Marketplace" htmlFor="cb-market">
                <select
                  id="cb-market"
                  className="min-h-11 rounded-ks-sm border border-ks-grey-3 bg-ks-surface px-2 text-ks-ink"
                  value={form.marketplace}
                  onChange={(e) => setForm({ ...form, marketplace: e.target.value })}
                >
                  {MARKETPLACES.map((m) => (
                    <option key={m} value={m}>
                      {marketplaceLabel(m)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="ASIN" htmlFor="cb-asin">
                <Input
                  id="cb-asin"
                  required
                  // Validated here as well as server-side: a 9-character ASIN is
                  // a typo worth catching before it becomes a silent no-collect.
                  pattern="[A-Za-z0-9]{10}"
                  title="A 10-character Amazon ASIN"
                  placeholder="B0XXXXXXXX"
                  className="min-h-11 w-40 font-mono"
                  value={form.competitor_asin}
                  onChange={(e) => setForm({ ...form, competitor_asin: e.target.value })}
                />
              </Field>

              <Field label="Brand" htmlFor="cb-brand">
                <Input
                  id="cb-brand"
                  className="min-h-11 w-40"
                  value={form.competitor_brand}
                  onChange={(e) => setForm({ ...form, competitor_brand: e.target.value })}
                />
              </Field>

              <Field label="Product" htmlFor="cb-product">
                <Input
                  id="cb-product"
                  className="min-h-11 w-48"
                  value={form.competitor_product}
                  onChange={(e) => setForm({ ...form, competitor_product: e.target.value })}
                />
              </Field>

              <Field label="Size (ml or g)" htmlFor="cb-size">
                <Input
                  id="cb-size"
                  className="min-h-11 w-28"
                  value={form.size_metric}
                  onChange={(e) => setForm({ ...form, size_metric: e.target.value })}
                />
              </Field>

              <Field label="Quantity" htmlFor="cb-qty">
                <Input
                  id="cb-qty"
                  type="number"
                  min="0"
                  step="any"
                  className="min-h-11 w-24"
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                />
              </Field>

              <label className="flex min-h-11 items-center gap-2 text-sm text-ks-ink">
                <input
                  type="checkbox"
                  checked={form.is_self}
                  onChange={(e) => setForm({ ...form, is_self: e.target.checked })}
                />
                Our own ASIN, excluded from the aggregates
              </label>

              <Button type="submit" disabled={busy}>
                Add to set
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="flex items-center gap-4">
        <h2 className="text-sm font-semibold text-ks-ink">Competitive set</h2>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
          />
          Show archived
        </label>
      </div>

      {entries.isPending && <Skeleton className="h-40 w-full" />}

      {entries.isError && (
        <Alert>
          <AlertTitle>The competitive set could not be loaded</AlertTitle>
          <AlertDescription>{messageFor(entries.error, areaName)}</AlertDescription>
        </Alert>
      )}

      {entries.data && entries.data.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            No entries yet. Add the first competitor above.
          </CardContent>
        </Card>
      )}

      {grouped.map(([marketplace, rows]) => (
        <Card key={marketplace}>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{marketplaceLabel(marketplace)} — our variant</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>ASIN</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((entry) => (
                  <TableRow key={entry.entry_id} className={entry.status === "archived" ? "opacity-60" : undefined}>
                    <TableCell className="text-muted-foreground">
                      {variantLabel.get(entry.variant_id) ?? entry.variant_id}
                    </TableCell>
                    <TableCell className="text-ks-ink">
                      {entry.competitor_brand ?? "Not recorded"}
                      {entry.is_self && (
                        <Badge variant="secondary" className="ml-2">
                          Ours
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {entry.competitor_product ?? "Not recorded"}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{entry.competitor_asin}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {[entry.size_metric, entry.quantity]
                        .filter((v) => v !== null && v !== "" && v !== undefined)
                        .join(" × ") || "Not recorded"}
                    </TableCell>
                    <TableCell>{entry.status}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        disabled={busy}
                        onClick={() =>
                          lifecycle.mutate({
                            entry,
                            action: entry.status === "active" ? "archive" : "restore",
                          })
                        }
                      >
                        {entry.status === "active" ? "Archive" : "Restore"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={htmlFor} className="text-xs text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}
